import CapitalProductProgress from 'pages/dashboard/components/Progress/components/CapitalProductProgress';
import { getApplicationStatuses } from 'pages/dashboard/selectors';
import { getDisplayedApplicationStatusTypeCode } from 'pages/Profile/selectors';
import { getTenantPhoneNumber } from 'theme/selectors';
import { useAppSelector } from 'hooks';
import Badge from 'components/Badge';
import {
  getActiveProductsTypeCodes,
  getIsCapitalProductActive,
} from 'pages/dashboard/components/Progress/selectors';
import {
  ClipboardCopyIcon,
  ProgressIcon,
  CloseCircleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from 'components/icons';
import {
  ApplicationStatuses,
  ApplicationStatusColors,
  ProductTypeCodes,
} from 'entities/progress';

const styles = {
  [ApplicationStatuses.pendingEnrollment]: {
    icon: <ClipboardCopyIcon className="text-yellow-500 mr-1 w-full h-full" />,
    color: ApplicationStatusColors[ApplicationStatuses.pendingEnrollment],
    description: 'text-yellow-700',
  },
  [ApplicationStatuses.pendingApproval]: {
    icon: <ProgressIcon className="text-blue-400 mr-1 w-full h-full" />,
    color: ApplicationStatusColors[ApplicationStatuses.pendingApproval],
    description: 'text-blue-700',
  },
  [ApplicationStatuses.suspended]: {
    icon: <CloseCircleIcon className="text-red-400 mr-1 w-full h-full" />,
    color: ApplicationStatusColors[ApplicationStatuses.suspended],
    description: 'text-red-700',
  },
  [ApplicationStatuses.declined]: {
    icon: <ExclamationCircleIcon className="text-red-400 mr-1 w-full h-full" />,
    color: ApplicationStatusColors[ApplicationStatuses.declined],
    description: 'text-red-700',
  },
  [ApplicationStatuses.approved]: {
    icon: <CheckCircleIcon className="text-green-400 mr-1 w-full h-full" />,
    color: ApplicationStatusColors[ApplicationStatuses.approved],
    description: 'text-green-700',
  },
  [ApplicationStatuses.complianceApproved]: {
    icon: <CheckCircleIcon className="text-green-400 mr-1 w-full h-full" />,
    color: ApplicationStatusColors[ApplicationStatuses.approved],
    description: 'text-green-700',
  },
};

type ApplicationStatusProps = {
  withDescription?: boolean;
};

const ApplicationStatus: React.FC<ApplicationStatusProps> = ({
  withDescription,
}) => {
  const statuses = useAppSelector(getApplicationStatuses);
  const tenantPhoneNumber = useAppSelector(getTenantPhoneNumber);

  const isCapitalProductActive = useAppSelector(getIsCapitalProductActive);
  const applicationStatusTypeCode = useAppSelector(
    getDisplayedApplicationStatusTypeCode,
  );
  const activeProducts = useAppSelector(getActiveProductsTypeCodes);

  if (!applicationStatusTypeCode) {
    return null;
  }

  if (
    isCapitalProductActive &&
    withDescription &&
    !activeProducts.includes(ProductTypeCodes.payments)
  ) {
    return <CapitalProductProgress />;
  }

  return (
    <Badge
      size={withDescription ? 'large' : 'small'}
      color={styles[applicationStatusTypeCode]?.color || 'blue'}
      className={withDescription ? 'px-3 py-3 w-full' : 'pl-[3px] pr-2'}
      rounded={`rounded-${withDescription ? 'md' : 'sm'}`}
    >
      <div className={withDescription ? 'flex' : 'flex items-center'}>
        {withDescription ? (
          <div className="w-5 h-5 flex-shrink-0">
            {styles[applicationStatusTypeCode]?.icon || null}
          </div>
        ) : (
          <div className="w-[14px] h-[14px] flex-shrink-0">
            {styles[applicationStatusTypeCode]?.icon || null}
          </div>
        )}
        <div className={withDescription ? 'pl-2' : 'pl-[2px]'}>
          <div className={withDescription ? 'mb-2' : ''}>
            {applicationStatusTypeCode}
          </div>
          {withDescription && (
            <div
              className={`${styles[applicationStatusTypeCode]?.description} font-normal text-justify pr-3`}
            >
              {statuses
                .find((s) => s.typeCode === applicationStatusTypeCode)
                ?.description?.replace('{{phone}}', tenantPhoneNumber)}
            </div>
          )}
        </div>
      </div>
    </Badge>
  );
};

export default ApplicationStatus;
